import SideModal from "/src/components/modal/SideModal/SideModal";
import NiceModal, { useModal } from "@ebay/nice-modal-react";
import ModalManager from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import useModalHelper from "/src/components/modal/hooks/useModalHelper";
import "./ConversationDocumentsModal.css";
import { useState, useEffect, useRef } from "react";
import ConversationDropzone from "../ConversationDropzone/ConversationDropzone";
import FileCard from "../FileCard/FileCard";
import DocumentService from "../../services/DocumentService";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import hasPermission from "/src/utils/hasPermission";
import storage from "/src/utils/storage";

const toUploadedItem = (doc) => ({
    documentId: doc.id,
    name: doc.filename,
    size: doc.size,
    type: doc.content_type,
    downloadUrl: doc.download_url,
    // The presigned download URL doubles as the thumbnail source for image
    // files reloaded from the backend (a fresh upload uses a local blob URL
    // instead, see handleFilesAdded/uploadDocument.then below).
    preview: doc.download_url,
    status: "uploaded",
});

const ConversationDocumentsModal = NiceModal.create(({ conversation, audioRef, onOpen, onCloseFromMenu }) => {
    const modal = useModal();
    const { onClose } = useModalHelper(modal);
    const toast = useToastDispatchContext();
    const callId = conversation?.call_id || conversation?.data?.call_id;
    const isClosed = conversation?.is_closed ?? conversation?.data?.is_closed;

    const user = storage('local').get("user");
    const canUpload = !isClosed && (user?.is_admin || hasPermission('upload_document'));
    const canDelete = !isClosed && (user?.is_admin || hasPermission('delete_document'));

    const handleClose = () => {
        onCloseFromMenu();
        onClose();
    }

    const [files, setFiles] = useState([]);
    // Local (blob:) preview URLs created for freshly-dropped files, tracked
    // separately so they're only revoked once, when the modal actually
    // closes — not on every files-state update (that was killing the
    // preview right after a fresh upload finished, before anyone even
    // clicked it: the cleanup from the "uploading" render was revoking the
    // very same blob URL carried over into the "uploaded" render).
    const blobUrlsRef = useRef(new Set());

    useEffect(() => {
        if (!callId) return;
        const fetchDocuments = async () => {
            const [response, error] = await DocumentService.getDocuments(callId);
            if (error || !response) return;
            setFiles(response.map(toUploadedItem));
        };
        fetchDocuments();
    }, [callId]);

    const handleFilesAdded = (newFiles) => {
        const filteredFiles = newFiles.filter(file => {
            const extension = file.name.split('.').pop().toLowerCase();
            return extension !== 'exe';
        });

        filteredFiles.forEach(file => {
            const preview = URL.createObjectURL(file);
            blobUrlsRef.current.add(preview);

            const localItem = {
                localId: `${file.name}-${file.lastModified}-${Math.random()}`,
                name: file.name,
                size: file.size,
                type: file.type,
                preview,
                status: "uploading",
            };
            setFiles(prev => [...prev, localItem]);

            const formData = new FormData();
            formData.append("file", file);

            DocumentService.uploadDocument(callId, formData).then(([response, error]) => {
                if (error || !response) {
                    setFiles(prev => prev.map(f => f.localId === localItem.localId ? { ...f, status: "error" } : f));
                    toast.error("Upload failed", `Could not upload "${file.name}".`);
                    return;
                }
                setFiles(prev => prev.map(f => f.localId === localItem.localId ? { ...toUploadedItem(response), preview: localItem.preview } : f));
            });
        });
    };

    const removeFile = async (fileToRemove) => {
        if (fileToRemove.status === "uploading") return;
        if (!canDelete) return;

        // Not persisted yet (upload failed before a documentId came back) —
        // nothing to confirm, just drop it locally.
        if (!fileToRemove.documentId) {
            setFiles((prev) => prev.filter((f) => f !== fileToRemove));
            return;
        }

        const result = await ModalManager.show(ApiModal, {
            title: "Delete " + fileToRemove.name,
            body: `Do you really want to delete "${fileToRemove.name}" ? This cannot be undone.`,
            api: { request: DocumentService.deleteDocument, param: [callId, fileToRemove.documentId] },
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Delete", type: "submit" },
            ],
            confirm: { name: fileToRemove.name, fieldName: "the document name" },
        });

        if (result?.error != null) {
            toast.error("Error", `Could not delete "${fileToRemove.name}".`);
            return;
        }

        toast.success("Document deleted", `"${fileToRemove.name}" has been removed.`);
        setFiles((prev) => prev.filter((f) => f !== fileToRemove));
    }

    useEffect(() => {
        return () => {
            blobUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    useEffect(() => {
        if (modal == null) return;
        onOpen(modal);
    }, [modal])
    return (
        <SideModal id={modal.id} autoClose={false} onClose={handleClose} style={{ width: '550px' }} name="conversation_transcription_modal">
            <div className="transcription-modal-leave">
                <div className="button-leave" id="close_modal_container" onClick={handleClose} >
                    <i id="close_modal" className="fa fa-times" aria-hidden="true"></i>
                </div>
            </div>
            {canUpload ? (
                <ConversationDropzone onFilesAdded={handleFilesAdded} />
            ) : (
                <div className="no-files">
                    {isClosed ? "Reopen the review to add documents." : "You don't have permission to add documents."}
                </div>
            )}
            <div className="file-preview-list">
                {files.length === 0 ? (
                    <div className="no-files">No attachment found</div>
                ) : (
                    files.map((item) => (
                        <FileCard key={item.documentId || item.localId} item={item} onRemove={removeFile} canRemove={canDelete} />
                    ))
                )}
            </div>
        </SideModal >
    )
});


export default ConversationDocumentsModal;
